import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Project, Steps } from "types";
import {
  possibleSteps,
  fallbackRequiredSteps,
  getRequiredStepsFromDirective,
} from "components/Progress/Steps";
import { useFeatureFlags } from "context/FeatureFlag";
import { Directive } from "pages/Backoffice/Questionaire/types";

export const useSteps = (project: Project | undefined) => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();

  const { steps, allStepsDone } = useMemo(() => {
    if (!project) return { steps: [], allStepsDone: false };

    const projectId = project?.projectdata?.crn;
    let requiredSteps: Steps[] =
      fallbackRequiredSteps[project.projectdata.category];

    requiredSteps = getRequiredStepsFromDirective(
      project.directives.map((d) => d.name),
      project.projectdata.category,
    );

    if (
      project.directives.find((d) => d.name === Directive.mvo) &&
      project.notifiedBody?.required
    ) {
      requiredSteps.splice(5, 0, Steps.notifiedBodies);
    }

    const steps = requiredSteps
      ?.filter(
        (stepKey) =>
          possibleSteps[stepKey] && stepKey !== Steps.supportingDocuments,
      )
      .map((stepKey) => {
        const stepDefition = possibleSteps[stepKey];

        return {
          ...stepDefition,
          key: stepKey,
          label: t(stepDefition.label),
          subline: t(stepDefition.subline),
          complete:
            stepDefition.completeFn(project).complete ||
            Boolean(stepDefition.isPreview?.(isFeatureEnabled)),
          location: stepDefition.location(projectId, project),
        };
      });

    return { steps, allStepsDone: steps?.every(({ complete }) => complete) };
  }, [project, t, isFeatureEnabled]);

  return { steps, allStepsDone };
};
